import React, { useEffect, useState } from 'react';
import {
    Container,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Button,
    Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    Typography,
    TablePagination,
} from "@mui/material";
import { useHistory } from 'react-router-dom';
import {getInsiderProgramsService} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";

const useStyles = makeStyles((theme) => ({
    container: {
        marginTop: theme.spacing(1),
        width: '100%',
        px: { xs: 0, sm: 2 },
        [theme.breakpoints.between('sm', 'md')]: {
            marginTop: '75px',
        },
    },
    tableRoot: {
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'separate',
        borderSpacing: '0px',
    },
    tableHeaderCell: {
        fontSize: '17px',
        fontWeight: 500,
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '12px',
        },
    },
    tableCell: {
        color: '#ED6628',
        textAlign: 'center',
        fontSize: '17px',
        padding: '16px',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: 0,
        [theme.breakpoints.down('sm')]: {
            wordBreak:'break-word',
            fontSize: '11px',
            padding: '12px !important',
        },
    },
    tableCellLeft: {
        color: '#ED6628',
        textAlign: 'left',
        fontSize: '17px',
        padding: '16px',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: 0,
        [theme.breakpoints.down('sm')]: {
            wordBreak:'break-word',
            fontSize: '11px',
            padding: '12px !important',
        },
    },
    descriptionCell: {
        color: '#ED6628',
        textAlign: 'left',
        fontSize: '17px',
        padding: '1px',
        [theme.breakpoints.down('sm')]: {
            wordBreak:'break-word',
            fontSize: '11px',
            padding: '1px !important',
        },
    },
    buttonCell: {
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            padding: '0 !important',
        },
    },

    title: {
        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
        color: 'transparent',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: '500 !important',
        textAlign: 'center',
        fontSize: '45px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: "32px !important",
        },
    },
    viewButton: {
        backgroundColor: "#ff6468",
        color: "#fff",
        fontWeight: 'bold',
        fontSize: '16px',
        borderRadius: '20px',
        boxShadow: 'none',
        padding:'10px 20px',
        textTransform: "none",
        '&:hover': {
            backgroundColor: "#ff6468",
            color: "#fff",
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '10px',
            padding: '4px 0px',
            borderRadius: '20px',
            width: '100%',
            display: 'block',
            lineHeight: '1.2',
            minWidth: 'unset',
        },
    }
}));

const DQProLearnerProgramList = () => {
    const classes = useStyles();
    const history = useHistory();
    const [learnerPrograms, setLearnerPrograms] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });

    useEffect(() => {
        fetchPrograms(page + 1, rowsPerPage);
    }, [page, rowsPerPage]);

    const fetchPrograms = (apiPage, limit) => {
        setLoading(true);
        getInsiderProgramsService(apiPage, limit).then(response => {
            console.log('Learning programs', response?.data?.learning_programs);
            console.log('Pagination data', response?.data?.pagination);
            const programs = response?.data?.learning_programs || [];
            const paginationData = response?.data?.pagination || {
                total: programs.length,
                page: apiPage,
                limit: limit,
                total_pages: 1,
                has_next: false,
                has_prev: false
            };

            setLearnerPrograms(programs);
            setPagination(paginationData);
            setLoading(false);
        }).catch((error) => {
            console.error('Error fetching programs:', error);
            setLearnerPrograms([]);
            setPagination({
                total: 0,
                page: apiPage,
                limit: limit,
                total_pages: 0,
                has_next: false,
                has_prev: false
            });
            setLoading(false);
        });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0); // Reset to first page when changing page size
    };

    const handleViewProgram = (program) => {
        history.push(`/dq_pro/products/program/dashboard/${program.code}`, { program });
    };

    // Function to truncate text to 3-4 words
    const truncateText = (text, wordLimit = 4) => {
        if (!text) return '';
        const words = text.split(' ');
        if (words.length <= wordLimit) return text;
        return words.slice(0, wordLimit).join(' ') + '...';
    };

    // Function to toggle description expansion
    const toggleDescription = (programCode) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [programCode]: !prev[programCode]
        }));
    };

    // Component for read more/less functionality
    const ReadMoreDescription = ({ description, programCode }) => {
        const isExpanded = expandedDescriptions[programCode];
        const truncatedText = truncateText(description);
        const shouldShowReadMore = description && description.split(' ').length > 4;

        if (!description) return <span>No description available</span>;

        if (!shouldShowReadMore) return <span>{description}</span>;

        return (
            <span>
                {isExpanded ? description : truncatedText}
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => toggleDescription(programCode)}
                    style={{
                        color: '#0000ff',
                        textDecoration: 'none',
                        marginLeft: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                    }}
                >
                    {isExpanded ? 'Read less' : 'Read more'}
                </Link>
            </span>
        );
    };

    // Function to extract date from string
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dateString.split('T')[0];
    };

    return (
        <Container className={classes.container} maxWidth={false}>
            <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>
                Your Learning Programs
            </Typography>
            <Typography variant="h5" sx={{ fontSize:{xs:'18px', sm:'1.5rem'},textAlign: 'center',p:{xs:'0 20px', sm:'0 30px', lg:'0 170px'},mx:'auto', mb: 4 }}>
            The following is a list of your created programs.
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: '#fff', width:'100%', maxWidth: '100vw', overflowX: 'auto', }}>
                <Table className={classes.tableRoot} aria-label="assessments table">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tableHeaderCell} style={{textAlign: 'left'}}>Program ID</TableCell>
                            <TableCell className={classes.tableHeaderCell} style={{textAlign: 'left'}}>Program Name</TableCell>
                            <TableCell className={classes.tableHeaderCell} style={{textAlign: 'left'}}>Course Description</TableCell>
                            <TableCell className={classes.tableHeaderCell}>Date Created</TableCell>
                            <TableCell className={classes.tableHeaderCell}>Time (min)</TableCell>
                            <TableCell className={classes.tableHeaderCell}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {learnerPrograms.length > 0 ? (
                            learnerPrograms.map((learnerProgram) => (
                                <TableRow key={learnerProgram.code}>
                                    <TableCell className={classes.tableCellLeft}>{learnerProgram.code}</TableCell>
                                    <TableCell className={classes.tableCellLeft}>{learnerProgram.full_name}</TableCell>
                                    <TableCell className={classes.descriptionCell}>
                                        <ReadMoreDescription
                                            description={learnerProgram.description}
                                            programCode={learnerProgram.code}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>{formatDate(learnerProgram.createdAt)}</TableCell>
                                    <TableCell className={classes.tableCell}>{learnerProgram.time_to_complete_minutes}</TableCell>
                                    <TableCell className={`${classes.tableCell} ${classes.buttonCell}`}>
                                        <Button
                                            className={classes.viewButton}
                                            onClick={() => handleViewProgram(learnerProgram)}
                                        >
                                            View Program
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No programs found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 25, 50]}
                    component="div"
                    count={pagination.total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
            <LoaderWithBackDrop loading={loading} />
        </Container>
    );
};

export default DQProLearnerProgramList;
